<template>
  <modal :show.sync="showModal" :clickToClose="false" :showClose="false" bodyClasses="" contentClasses="">
    <!-- Header del modal -->
    <div class="custom-modal-header">
      <h4 class="modal-title font-weight-bold text-dark">
        <i class="fa fa-camera-retro mr-2 text-primary"></i>
        Captura de Fotografía
      </h4>
      <button type="button" class="close" @click="showModal = false">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Opciones iniciales -->
    <div v-if="!pictureIsTaken" class="p-3 p-md-4">
      <p class="text-muted text-center mb-3 mb-md-4 small">
        Selecciona cómo deseas agregar la fotografía del socio
      </p>
      
      <div class="row">
        <div class="col-6 mb-3">
          <div class="option-card card shadow-sm border-0 h-100" @click="$refs.avatarInput.click()">
            <div class="card-body text-center py-3 py-md-5">
              <div class="icon-wrapper mb-2 mb-md-3">
                <i class="fa fa-cloud-upload fa-3x fa-md-4x text-primary"></i>
              </div>
              <h6 class="h5-md font-weight-bold mb-1 mb-md-2">Subir Archivo</h6>
              <p class="text-muted small mb-0 d-none d-md-block">
                Selecciona una imagen desde tu dispositivo
              </p>
              <input
                type="file"
                @change="handleFileUpload"
                ref="avatarInput"
                accept="image/jpeg,image/jpg,image/png"
                class="d-none"
              />
            </div>
          </div>
        </div>
        
        <div class="col-6 mb-3">
          <div class="option-card card shadow-sm border-0 h-100" @click="setupCamera">
            <div class="card-body text-center py-3 py-md-5">
              <div class="icon-wrapper mb-2 mb-md-3">
                <i class="fa fa-video fa-3x fa-md-4x text-info"></i>
              </div>
              <h6 class="h5-md font-weight-bold mb-1 mb-md-2">Usar Cámara</h6>
              <p class="text-muted small mb-0 d-none d-md-block">
                Captura una foto con tu cámara web
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Vista de cámara -->
    <div v-else class="p-3 p-md-4">
      <div class="camera-container position-relative">
        <!-- Loading mientras se carga la cámara -->
        <div v-if="!webcamIsReady" class="camera-loading d-flex align-items-center justify-content-center">
          <div class="text-center">
            <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;">
              <span class="sr-only">Cargando cámara...</span>
            </div>
            <p class="text-muted">Iniciando cámara...</p>
          </div>
        </div>
        
        <!-- Video y Canvas para face detection -->
        <div class="video-wrapper position-relative" :class="{ 'd-none': !webcamIsReady }">
          <video
            ref="cameraPreview"
            autoplay
            playsinline
            class="video-feed"
          ></video>
          <canvas 
            ref="faceCanvas" 
            class="face-overlay"
            v-show="faceDetectionEnabled"
          ></canvas>
          
          <!-- Indicador de detección facial -->
          <div v-if="faceDetectionEnabled && webcamIsReady" class="face-indicator">
            <span 
              class="badge"
              :class="faceDetected ? 'badge-success' : 'badge-warning'"
            >
              <i :class="faceDetected ? 'fa fa-check-circle' : 'fa fa-exclamation-circle'" class="mr-1"></i>
              {{ faceDetected ? 'Rostro detectado' : 'Buscando rostro...' }}
            </span>
          </div>
        </div>
        
        <!-- Canvas oculto para captura -->
        <canvas ref="canvas" class="d-none"></canvas>
      </div>
      
      <!-- Controles de la cámara -->
      <div v-if="webcamIsReady" class="mt-3 mt-md-4">
        <!-- Selector de Resolución -->
        <div class="d-flex align-items-center justify-content-center mb-3 mb-md-4">
          <div class="resolution-selector">
            <label class="small text-muted mb-1 d-block text-center">
              <i class="fa fa-image mr-1"></i>
              Calidad de Imagen
            </label>
            <el-select
              v-model="selectedResolution"
              placeholder="Seleccionar resolución"
              size="small"
              class="w-100"
              @change="handleResolutionChange"
            >
              <el-option
                v-for="res in availableResolutions"
                :key="res.value"
                :label="res.label"
                :value="res.value"
              >
                <span class="d-flex align-items-center">
                  <i class="fa fa-image mr-2"></i>
                  {{ res.label }}
                </span>
              </el-option>
            </el-select>
            <small class="text-muted d-block text-center mt-1">
              {{ currentResolution.width }}x{{ currentResolution.height }}px
            </small>
          </div>
        </div>
        
        <!-- Switch para reconocimiento facial -->
        <div class="d-flex align-items-center justify-content-center mb-3 mb-md-4">
          <label class="custom-switch mb-0">
            <input 
              type="checkbox" 
              v-model="faceDetectionEnabled"
              @change="toggleFaceDetection"
            >
            <span class="custom-switch-indicator"></span>
            <span class="custom-switch-description ml-2">
              <i class="fa fa-magic mr-1 d-none d-sm-inline"></i>
              <span class="d-none d-sm-inline">Reconocimiento</span> Facial
              <small class="text-muted d-none d-md-block">
                Mejora la calidad detectando el rostro
              </small>
            </span>
          </label>
        </div>
        
        <!-- Botones de acción -->
        <div class="d-flex justify-content-center">
          <button
            @click="capturePhoto"
            class="btn btn-primary px-3 px-md-4 mr-2 mr-md-3"
            :disabled="faceDetectionEnabled && !faceDetected"
          >
            <i class="fa fa-camera mr-1 mr-md-2"></i>
            <span class="d-none d-sm-inline">Capturar</span> Foto
          </button>
          <button
            @click="cancelCamera"
            class="btn btn-outline-secondary px-3 px-md-4"
          >
            <i class="fa fa-arrow-left mr-1 mr-md-2"></i>
            Cancelar
          </button>
        </div>
        
        <!-- Consejos -->
        <div v-if="faceDetectionEnabled" class="mt-3 text-center">
          <small class="text-muted">
            <i class="fa fa-info-circle mr-1"></i>
            Centra tu rostro en el recuadro para mejor calidad
          </small>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
import { Modal } from 'src/components/UIComponents';
import { Select, Option } from 'element-ui';

// Face-api se cargará dinámicamente desde CDN
let faceapi = null;

export default {
  name: 'SocioAvatar',
  components: {
    Modal,
    [Select.name]: Select,
    [Option.name]: Option
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showModal: false,
      pictureIsTaken: false,
      webcamIsReady: false,
      avatarPlaceholder: null,
      faceDetectionEnabled: false,
      faceDetected: false,
      faceDetectionInterval: null,
      modelsLoaded: false,
      isLoadingModels: false,
      // Resolución de captura
      selectedResolution: '800x600',
      availableResolutions: [
        { label: '800x600 (Estándar)', value: '800x600', width: 800, height: 600 },
        { label: '1280x720 (HD)', value: '1280x720', width: 1280, height: 720 }
      ]
    };
  },
  computed: {
    currentResolution() {
      return this.availableResolutions.find(r => r.value === this.selectedResolution) || this.availableResolutions[0];
    }
  },
  watch: {
    show(newVal) {
      this.showModal = newVal;
    },
    showModal(newVal) {
      if (!newVal) {
        // Cleanup cuando se cierra el modal
        this.cleanup();
        this.$emit('close');
      }
    }
  },
  mounted() {
    // Cargar el avatar placeholder desde el store si está disponible
    this.avatarPlaceholder = this.$store.getters.getAvatarPlaceHolder || '';
  },
  methods: {
    async loadFaceApiModels() {
      if (this.modelsLoaded || this.isLoadingModels) return;
      
      this.isLoadingModels = true;
      try {
        // Cargar face-api.js desde CDN si no está cargado
        if (!faceapi) {
          await this.loadFaceApiScript();
        }
        
        // Cargar los modelos desde CDN
        const MODEL_URL = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/model';
        
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL)
        ]);
        
        this.modelsLoaded = true;
      } catch (error) {
        console.error('Error cargando modelos de Face API:', error);
      } finally {
        this.isLoadingModels = false;
      }
    },
    
    loadFaceApiScript() {
      return new Promise((resolve, reject) => {
        // Verificar si ya existe el script
        if (document.getElementById('faceapi-script')) {
          if (window.faceapi) {
            faceapi = window.faceapi;
            resolve();
          } else {
            // Esperar a que se cargue
            const checkInterval = setInterval(() => {
              if (window.faceapi) {
                faceapi = window.faceapi;
                clearInterval(checkInterval);
                resolve();
              }
            }, 100);
            
            // Timeout después de 10 segundos
            setTimeout(() => {
              clearInterval(checkInterval);
              reject(new Error('Timeout cargando face-api.js'));
            }, 10000);
          }
          return;
        }
        
        const script = document.createElement('script');
        script.id = 'faceapi-script';
        script.src = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api/dist/face-api.min.js';
        script.async = true;
        
        script.onload = () => {
          faceapi = window.faceapi;
          resolve();
        };
        
        script.onerror = () => {
          reject(new Error('Error al cargar face-api.js desde CDN'));
        };
        
        document.head.appendChild(script);
      });
    },
    
    cleanup() {
      // Detener detección facial
      if (this.faceDetectionInterval) {
        clearInterval(this.faceDetectionInterval);
        this.faceDetectionInterval = null;
      }
      
      // Limpiar canvas
      const faceCanvas = this.$refs.faceCanvas;
      if (faceCanvas) {
        const ctx = faceCanvas.getContext('2d');
        ctx.clearRect(0, 0, faceCanvas.width, faceCanvas.height);
      }
      
      // Detener la cámara si está activa
      const video = this.$refs.cameraPreview;
      if (video && video.srcObject) {
        video.srcObject.getTracks().forEach((track) => track.stop());
        video.srcObject = null;
      }
      
      this.pictureIsTaken = false;
      this.webcamIsReady = false;
      this.faceDetected = false;
      this.faceDetectionEnabled = false;
    },
    
    async setupCamera() {
      this.pictureIsTaken = true;
      this.webcamIsReady = false;
      
      // Esperar al siguiente ciclo de renderizado
      await this.$nextTick();
      
      if (!this.$refs.cameraPreview) {
        console.error('El elemento de video no está disponible.');
        this.pictureIsTaken = false;
        return;
      }
      
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: this.currentResolution.width },
            height: { ideal: this.currentResolution.height }
          }
        });
        
        this.$refs.cameraPreview.srcObject = stream;
        
        // Esperar a que el video esté listo
        this.$refs.cameraPreview.onloadedmetadata = () => {
          this.webcamIsReady = true;
          this.setupFaceCanvas();
        };
      } catch (error) {
        console.error('Error al acceder a la cámara del dispositivo:', error.message || error);
        this.$swal.fire({
          title: 'Error',
          text: 'No se pudo acceder a la cámara. Verifica los permisos.',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
        this.pictureIsTaken = false;
      }
    },
    
    setupFaceCanvas() {
      const video = this.$refs.cameraPreview;
      const faceCanvas = this.$refs.faceCanvas;
      
      if (video && faceCanvas) {
        faceCanvas.width = video.videoWidth;
        faceCanvas.height = video.videoHeight;
      }
    },
    
    async toggleFaceDetection() {
      if (this.faceDetectionEnabled) {
        // Verificar si los modelos están cargados
        if (!this.modelsLoaded) {
          this.$swal.fire({
            title: 'Cargando...',
            text: 'Los modelos de reconocimiento facial se están cargando. Por favor espera.',
            icon: 'info',
            timer: 3000,
            showConfirmButton: false
          });
          
          await this.loadFaceApiModels();
          
          if (!this.modelsLoaded) {
            this.faceDetectionEnabled = false;
            return;
          }
        }
        
        this.startFaceDetection();
      } else {
        this.stopFaceDetection();
      }
    },
    
    startFaceDetection() {
      if (this.faceDetectionInterval) return;
      
      const video = this.$refs.cameraPreview;
      const faceCanvas = this.$refs.faceCanvas;
      
      this.faceDetectionInterval = setInterval(async () => {
        if (video && faceCanvas) {
          const detections = await faceapi
            .detectAllFaces(video, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks(true);
          
          const ctx = faceCanvas.getContext('2d');
          ctx.clearRect(0, 0, faceCanvas.width, faceCanvas.height);
          
          if (detections.length > 0) {
            this.faceDetected = true;
            
            // Dibujar el recuadro del rostro
            detections.forEach(detection => {
              const box = detection.detection.box;
              
              // Dibujar recuadro
              ctx.strokeStyle = '#28a745';
              ctx.lineWidth = 3;
              ctx.strokeRect(box.x, box.y, box.width, box.height);
              
              // Dibujar puntos faciales
              const landmarks = detection.landmarks;
              const positions = landmarks.positions;
              
              ctx.fillStyle = '#28a745';
              positions.forEach(point => {
                ctx.beginPath();
                ctx.arc(point.x, point.y, 2, 0, 2 * Math.PI);
                ctx.fill();
              });
            });
          } else {
            this.faceDetected = false;
          }
        }
      }, 100);
    },
    
    stopFaceDetection() {
      if (this.faceDetectionInterval) {
        clearInterval(this.faceDetectionInterval);
        this.faceDetectionInterval = null;
      }
      
      // Limpiar el canvas
      const faceCanvas = this.$refs.faceCanvas;
      if (faceCanvas) {
        const ctx = faceCanvas.getContext('2d');
        ctx.clearRect(0, 0, faceCanvas.width, faceCanvas.height);
      }
      
      this.faceDetected = false;
    },
    
    async capturePhoto() {
      const canvas = this.$refs.canvas;
      const context = canvas.getContext('2d');
      const video = this.$refs.cameraPreview;
      
      // Configurar el canvas con las dimensiones del video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      
      // Obtener la foto original como Base64
      const originalBase64 = canvas.toDataURL('image/jpeg');
      
      // Calcular las nuevas dimensiones para la miniatura
      const maxSize = 200; // Máximo tamaño para la miniatura (200px para mejor calidad)
      let width = video.videoWidth;
      let height = video.videoHeight;
      
      if (width > height) {
        if (width > maxSize) {
          height = height * (maxSize / width);
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = width * (maxSize / height);
          height = maxSize;
        }
      }
      
      // Ajustar el tamaño del canvas para la miniatura
      canvas.width = width;
      canvas.height = height;
      context.drawImage(video, 0, 0, width, height);
      
      // Obtener la miniatura como Base64
      const thumbnailBase64 = canvas.toDataURL('image/jpeg');
      
      // Emitir el evento con las imágenes
      this.$emit('avatar-captured', {
        imagenPerfil: this.trimBase64String(originalBase64),
        imagenMiniatura: this.trimBase64String(thumbnailBase64)
      });
      
      // Cerrar el modal
      this.showModal = false;
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
        this.$swal.fire({
          title: 'Error',
          text: 'Sólo puedes subir imágenes PNG o JPG',
          icon: 'error',
          confirmButtonColor: '#6bd098'
        });
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Crear canvas para la imagen original en JPEG
          const canvasOriginal = document.createElement('canvas');
          canvasOriginal.width = img.width;
          canvasOriginal.height = img.height;
          const ctxOriginal = canvasOriginal.getContext('2d');
          ctxOriginal.drawImage(img, 0, 0, img.width, img.height);
          const originalJPEGBase64 = canvasOriginal.toDataURL('image/jpeg');
          
          // Crear miniatura
          const canvas = document.createElement('canvas');
          const maxSize = 200; // Máximo tamaño para la miniatura (200px para mejor calidad)
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height *= maxSize / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width *= maxSize / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          const thumbnailBase64 = canvas.toDataURL('image/jpeg');
          
          // Emitir el evento con las imágenes
          this.$emit('avatar-captured', {
            imagenPerfil: this.trimBase64String(originalJPEGBase64),
            imagenMiniatura: this.trimBase64String(thumbnailBase64)
          });
          
          // Cerrar el modal
          this.showModal = false;
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    
    trimBase64String(base64String) {
      const base64DataStart = base64String.indexOf('base64,') + 'base64,'.length;
      if (base64DataStart === -1 + 'base64,'.length) {
        console.error('La cadena no contiene datos Base64 válidos.');
        return '';
      }
      return base64String.substring(base64DataStart);
    },
    
    async handleResolutionChange() {
      // Reiniciar la cámara con la nueva resolución
      if (this.webcamIsReady) {
        // Detener la cámara actual
        if (this.$refs.cameraPreview && this.$refs.cameraPreview.srcObject) {
          this.$refs.cameraPreview.srcObject.getTracks().forEach((track) => track.stop());
          this.$refs.cameraPreview.srcObject = null;
        }
        
        // Reiniciar con nueva resolución
        this.webcamIsReady = false;
        await this.$nextTick();
        await this.setupCamera();
      }
    },
    
    cancelCamera() {
      // Detener detección facial si está activa
      this.stopFaceDetection();
      
      // Detener la cámara
      if (this.$refs.cameraPreview && this.$refs.cameraPreview.srcObject) {
        this.$refs.cameraPreview.srcObject.getTracks().forEach((track) => track.stop());
        this.$refs.cameraPreview.srcObject = null;
      }
      
      // Volver a las opciones iniciales
      this.pictureIsTaken = false;
      this.webcamIsReady = false;
      this.faceDetectionEnabled = false;
    }
  },
  
  beforeDestroy() {
    this.cleanup();
  }
};
</script>

<style scoped>
/* Modal centering and sizing */
::v-deep .modal {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  z-index: 9999 !important;
}

::v-deep .modal-dialog {
  max-width: 900px !important;
  width: 95% !important;
  margin: auto !important;
  position: relative !important;
}

@media (min-width: 768px) {
  ::v-deep .modal-dialog {
    width: 90% !important;
  }
}

@media (min-width: 1200px) {
  ::v-deep .modal-dialog {
    width: 85% !important;
    max-width: 1000px !important;
  }
}

::v-deep .modal-content {
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2) !important;
  padding: 0 !important;
}

::v-deep .modal-header {
  display: none !important;
}

::v-deep .modal .modal-header {
  display: none !important;
}

::v-deep .modal-dialog .modal-header {
  display: none !important;
}

::v-deep .modal-content > .modal-header {
  display: none !important;
}

::v-deep .modal-backdrop {
  background-color: rgba(0, 0, 0, 0.7) !important;
}

/* Asegurar que el body del modal tenga el padding correcto */
::v-deep .modal-body {
  padding: 0 !important;
  position: relative;
}

::v-deep .modal-body:first-child {
  padding-top: 0 !important;
}

::v-deep .modal-content > :first-child {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Asegurar que el header personalizado sea el primer elemento */
.custom-modal-header:first-child {
  margin-top: 0 !important;
}

/* Modal header personalizado */
.custom-modal-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 12px 12px 0 0;
  width: 100%;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  padding: 1rem 1.5rem;
  margin: 0;
  position: relative;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.custom-modal-header .close {
  font-size: 1.5rem;
  line-height: 1;
  color: #000;
  opacity: 0.5;
  text-shadow: none;
}

.custom-modal-header .close:hover {
  opacity: 0.8;
}

/* Option cards */
.option-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.option-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1) !important;
}

.option-card:hover .icon-wrapper i {
  transform: scale(1.1);
}

.icon-wrapper {
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border-radius: 50%;
  background: rgba(0, 123, 255, 0.1);
}

@media (min-width: 768px) {
  .icon-wrapper {
    width: 100px;
    height: 100px;
  }
  
  .fa-md-4x {
    font-size: 4rem !important;
  }
  
  .h5-md {
    font-size: 1.25rem !important;
  }
}

.icon-wrapper i {
  transition: transform 0.3s ease;
}

/* Camera container */
.camera-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 10px;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (min-width: 768px) {
  .camera-container {
    padding: 20px;
    min-height: 480px;
  }
}

@media (min-width: 1200px) {
  .camera-container {
    min-height: 580px;
  }
}

.camera-loading {
  width: 100%;
  height: 300px;
}

@media (min-width: 768px) {
  .camera-loading {
    height: 480px;
  }
}

@media (min-width: 1200px) {
  .camera-loading {
    height: 580px;
  }
}

/* Video feed */
.video-wrapper {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

@media (min-width: 768px) {
  .video-wrapper {
    max-width: 90%;
  }
}

@media (min-width: 1200px) {
  .video-wrapper {
    max-width: 85%;
  }
}

.video-feed {
  width: 100%;
  height: auto;
  display: block;
  transform: scaleX(-1); /* Espejo para selfie */
}

/* Face detection overlay */
.face-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  transform: scaleX(-1); /* Espejo para coincidir con el video */
}

/* Face indicator */
.face-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
}

.face-indicator .badge {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

@media (min-width: 768px) {
  .face-indicator .badge {
    padding: 8px 16px;
    font-size: 14px;
  }
}

/* Custom switch */
.custom-switch {
  padding-left: 2.25rem;
  position: relative;
  cursor: pointer;
}

.custom-switch input[type="checkbox"] {
  position: absolute;
  opacity: 0;
  cursor: pointer;
}

.custom-switch-indicator {
  position: absolute;
  top: 2px;
  left: 0;
  width: 40px;
  height: 22px;
  background: #ced4da;
  border-radius: 20px;
  transition: background-color 0.3s ease;
}

.custom-switch-indicator:before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  transition: transform 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.custom-switch input[type="checkbox"]:checked ~ .custom-switch-indicator {
  background: #28a745;
}

.custom-switch input[type="checkbox"]:checked ~ .custom-switch-indicator:before {
  transform: translateX(18px);
}

.custom-switch-description {
  font-weight: 500;
  color: #495057;
}

.custom-switch-description small {
  font-weight: 400;
  color: #6c757d;
}

/* Resolution selector */
.resolution-selector {
  min-width: 200px;
  max-width: 280px;
  margin: 0 auto;
  padding: 1rem;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.resolution-selector .el-select {
  width: 100%;
}

.resolution-selector .el-select >>> .el-input__inner {
  border-radius: 6px;
  border-color: #667eea;
  text-align: center;
  font-weight: 500;
}

.resolution-selector .el-select >>> .el-input__inner:focus {
  border-color: #764ba2;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
}

@media (max-width: 576px) {
  .resolution-selector {
    min-width: 180px;
    padding: 0.75rem;
  }
}

/* Buttons */
.btn-lg {
  border-radius: 8px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  ::v-deep .modal-dialog {
    width: 95% !important;
    margin: 1rem auto !important;
  }
  
  ::v-deep .modal-content {
    margin: 0 !important;
  }
}

@media (max-width: 576px) {
  ::v-deep .modal-dialog {
    width: 95% !important;
    max-width: 100% !important;
    margin: 1rem auto !important;
  }
  
  .custom-modal-header {
    padding: 0.75rem 1rem !important;
  }
  
  .custom-modal-header h4 {
    font-size: 1.1rem !important;
  }
  
  .custom-modal-header h4 i {
    font-size: 1rem !important;
  }
  
  .p-3 {
    padding: 0.75rem !important;
  }
  
  .option-card .card-body {
    padding: 1.5rem 0.5rem !important;
  }
  
  .option-card h6 {
    font-size: 0.9rem !important;
  }
  
  .icon-wrapper {
    width: 50px;
    height: 50px;
  }
  
  .icon-wrapper i {
    font-size: 1.5rem !important;
  }
  
  .camera-container {
    padding: 10px;
  }
  
  .btn {
    font-size: 0.875rem !important;
    padding: 0.5rem 1rem !important;
  }
  
  .btn-lg {
    font-size: 0.875rem !important;
    padding: 0.5rem 1rem !important;
  }
  
  .custom-switch-description {
    font-size: 0.875rem !important;
  }
}
</style>
